import React, { useState } from "react";
import {
    LayoutDashboard,
    ShoppingCart,
    Store,
    Image,
    Users,
    ChevronDown,
    ChevronRight,
    LogOut,
    Bell,
    Package,
    CreditCard,
    Calculator,
    Tag,
    Heart,
    CalendarSync,
    Truck,
    User,
    ChartSpline,
    CircleQuestionMark,
    Cog,
} from 'lucide-react';
import Logo from "@shared/Assets/Image/miniLogo.png";
import {
    Box,
    Drawer,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Collapse,
    Typography,
    Button,
} from "@mui/material";
import { NavLink, useLocation } from "react-router-dom";
import { sidenavStyle } from "../../styles/layout";
import { useTheme } from "@mui/material/styles";
import LogoutModel from "./logoutModel";
import { ApiConfig } from "@shared/Config/config";

const navItems = [
    { id: "Dashboard", label: "Dashboard", icon: LayoutDashboard, screen: "dashboard", path: "dashboard" },
    { id: "Vendors", label: "Vendors", icon: Store, screen: "tasks", path: "vendor" },
    { id: "Dispatchers", label: "Dispatchers", icon: Users, screen: "sub-admin", path: "sub-admin" },
    { id: "Delivery Products", label: "Delivery Products", icon: Package, screen: "products", path: "products" },
    { id: "Master Products", label: "Master Products", icon: Cog, screen: "products", path: "master" },
    { id: "Orders", label: "Orders", icon: ShoppingCart, screen: "customers", path: "orders" },
    { id: "Customers", label: "Customers", icon: Users, screen: "profile", path: "customers" },
    { id: "Banners", label: "Banners", icon: Image, screen: "agents", path: "banner" },
    { id: "Profile", label: "Profile", icon: User, screen: "profile", path: "profile" },
    { id: "Refund Details", label: "Refund Details", icon: CalendarSync, screen: "agents", path: "refund" },
    { id: "Price Rules", label: "Price Rules", icon: CreditCard, screen: "agents", path: "price-rules" },
    { id: "Tax", label: "Tax", icon: Calculator, screen: "agents", path: "tax" },
    { id: "Promo Codes", label: "Promo Codes", icon: Tag, screen: "agents", path: "promo-codes" },
    { id: "Loyalty", label: "Loyalty", icon: Heart, screen: "agents", path: "loyalty" },
    { id: "Notifications", label: "Notifications", icon: Bell, screen: "agents", path: "notifications" },
    { id: "Analytics", label: "Analytics", icon: ChartSpline, screen: "analytics", path: "analytics" },
    { id: "Agent Analytics", label: "Agent Analytics", icon: Truck, screen: "teams", path: "agentOverview" },
    { id: "FAQ", label: "FAQ", icon: CircleQuestionMark, screen: "faq", path: "faq" },
    { id: "Contact Support", label: "Contact Support", icon: Bell, screen: "contactSupport", path: "contact-support" },
];

export default function Sidebar({ onLogout }) {
    const theme = useTheme();
    const classes = sidenavStyle(theme);
    const [expandedItems, setExpandedItems] = useState(new Set(["analytics"]));
    const [openDialog, setOpenDialog] = useState(false);
    const location = useLocation();


    const toggleExpanded = (itemId) => {
        setExpandedItems((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(itemId)) {
                newSet.delete(itemId);
            } else {
                newSet.add(itemId);
            }
            return newSet;
        });
    };

    const renderNavItem = (item, depth = 0) => {
        const isExpanded = expandedItems.has(item.id);
        const hasChildren = item.children && item.children.length > 0;
        return (
            <React.Fragment key={item.id}>
                <>
                    <NavLink to={item.path}>
                        <ListItemButton
                            onClick={() => {
                                if (hasChildren) {
                                    toggleExpanded(item.id);
                                }
                            }}
                            sx={location.pathname.includes(item.path) ? classes.active : classes.inactive}
                        >
                            {/* {item.path}  {pl: depth > 0 ? 2 + depth * 2 : 2}*/}

                            <ListItemIcon sx={{ minWidth: 32 }}>
                                <item.icon
                                    size={18}
                                    color={location.pathname.includes(item.path) ? theme.palette.primary.main : '#968f8c'}
                                />
                            </ListItemIcon>
                            <ListItemText
                                primary={item.label}
                                sx={{
                                    '& .MuiListItemText-primary': {
                                        fontWeight: 600,
                                        fontSize: 14,
                                        color: location.pathname.includes(item.path)
                                            ? theme.palette.primary.main
                                            : "#968f8c"
                                    }
                                }}
                            />
                            {hasChildren &&
                                (isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />)}
                        </ListItemButton>
                    </NavLink>
                </>

                {hasChildren && (
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                            {item?.children?.map((child) => renderNavItem(child, depth + 1))}
                        </List>
                    </Collapse>
                )}
            </React.Fragment>
        );
    };

    return (
        <Drawer variant="permanent" sx={classes.drawerDiv}>
            {/* Logo */}
            <Box sx={classes.sidenavHeader}>
                <img src={Logo} alt="Logo" style={classes.logo} />
                <Box>
                    <Typography variant="subtitle1" fontWeight="600">
                        Super Admin
                    </Typography>
                </Box>
            </Box>

            <LogoutModel openDialog={openDialog} setOpenDialog={setOpenDialog} onLogout={onLogout} />

            <Box sx={classes.sidenavBody}>
                <List>
                    {navItems?.map((item) => renderNavItem(item))}
                </List>

                <Box sx={classes.versionDiv}>
                    <Typography variant="body2" align="center">
                        {ApiConfig?.ver_superadmin}
                    </Typography>
                </Box>

                <Box sx={{ px: 2 }}>
                    <Button
                        variant="outlined"
                        fullWidth
                        startIcon={<LogOut size={18} />}
                        onClick={() => setOpenDialog(true)}
                    >
                        Sign Out
                    </Button>
                </Box>
            </Box>
        </Drawer>
    );
}
